"use client";

import { NextSpecial } from "@/data/v2/models/autoplay";
import { getNextSpecials } from "@/data/v2/client/user/autoplay";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "@punchup/data/legacy/hooks";

interface UseAutoplayNextOptions {
  /** The currently-playing special to recommend a queue from. */
  specialId?: string;
  /** Prior specials played this session, oldest→newest. */
  sessionPlayedIds?: string[];
  /** Max results (server clamps to 1..25). */
  limit?: number;
  /** Override the default enabled gating (still requires a logged-in user + specialId). */
  enabled?: boolean;
}

/**
 * Fetches the ranked autoplay "what plays next" queue for the comedian page.
 *
 * Requires a logged-in user (the endpoint is auth-gated and the recommendation
 * is personalized). The query key includes the session so the queue re-ranks as
 * specials are played and diversity decay kicks in.
 */
export function useAutoplayNext({
  specialId,
  sessionPlayedIds = [],
  limit,
  enabled = true,
}: UseAutoplayNextOptions) {
  const user = useUser();

  return useQuery<NextSpecial[]>({
    queryKey: ["autoplay-next", specialId, sessionPlayedIds, limit],
    queryFn: () =>
      getNextSpecials(specialId as string, sessionPlayedIds, limit),
    enabled: enabled && !!user?.id && !!specialId,
    staleTime: 5 * 60 * 1000, // 5 minutes — per-user-per-session cache.
    gcTime: 10 * 60 * 1000,
  });
}
